import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';
import PropTypes from 'prop-types';

const ERROR_TYPE_TITLES = {
  upload: 'Upload Failed',
  missing_t2: 'Missing T2',
  artefacts_detected: 'Bad Quality Image',
  download: 'Unable to Load File',
  processing: 'ERROR',
};

function UploadErrorContent({ patientId }) {
  return (
    <DialogContentText component="div">
      <Typography gutterBottom>
        Some files could not be uploaded due to an unexpected error.
      </Typography>
      <Typography gutterBottom sx={{ mt: 1 }}>
        <strong>How to resolve:</strong>
      </Typography>
      <Box component="ul" sx={{ mt: 0, pl: 3 }}>
        <Typography component="li">Check your internet connection</Typography>
        <Typography component="li">Try uploading the files again</Typography>
      </Box>
      <Typography gutterBottom sx={{ mt: 1 }}>
        If the issue continues, please contact{' '}
        <Link href="mailto:service@replico.tech">service@replico.tech</Link>{' '}
        and include the internal patient ID in your email.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Internal patient ID: {patientId}
      </Typography>
    </DialogContentText>
  );
}

function DownloadErrorContent({ patientId }) {
  return (
    <DialogContentText component="div">
      <Typography gutterBottom>
        Some files could not be loaded due to an unexpected error.
      </Typography>
      <Typography gutterBottom sx={{ mt: 1 }}>
        If the issue continues, please contact{' '}
        <Link href="mailto:service@replico.tech">service@replico.tech</Link>{' '}
        and include the internal patient ID in your email.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Internal patient ID: {patientId}
      </Typography>
    </DialogContentText>
  );
}

function ProcessingErrorContent({ stepLabel, patientId }) {
  return (
    <DialogContentText>
      Unexpected error at step: <strong>{stepLabel}</strong>.<br />
      Please contact <Link href="mailto:service@replico.tech">service@replico.tech</Link>,
      including the internal patient ID in the e-mail.<br />
      Internal patient ID: {patientId}
    </DialogContentText>
  );
}

function MissingT2ErrorContent({ patientId }) {
  return (
    <DialogContentText component="div">
      <Typography gutterBottom>
        We could not find any T2 sequence matching the requirements. 
      </Typography>
      <Typography gutterBottom>
        Please check the sequence requirements in the IFU and try to reupload.
      </Typography>
      <Typography gutterBottom sx={{ mt: 1 }}>
        Please contact{' '}
        <Link href="mailto:service@replico.tech">service@replico.tech</Link>{' '}
        and include the internal patient ID in your email for more information.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Internal patient ID: {patientId}
      </Typography>
    </DialogContentText>
  )
}

function ArtefactsErrorContent({ patientId }) {
  return (
    <DialogContentText component="div">
      <Typography gutterBottom>
        The sequence you uploaded did not pass our quality check.
      </Typography>
      <Typography gutterBottom>
        The segmentation will not be performed since the quality of results cannot be guaranteed.
      </Typography>
      <Typography gutterBottom sx={{ mt: 1 }}>
        Please contact{' '}
        <Link href="mailto:service@replico.tech">service@replico.tech</Link>{' '}
        and include the internal patient ID in your email for more information.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Internal patient ID: {patientId}
      </Typography>
    </DialogContentText>
  )
}

export default function ProcessingErrorDialog({
  open,
  onClose,
  patientId = '',
  stepLabel = '',
  errorType = 'processing',
}) {
  return (
    <Dialog open={open} onClose={onClose} disableScrollLock>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ErrorIcon sx={{ color: 'error.light' }} />
        {ERROR_TYPE_TITLES[errorType]}
      </DialogTitle>
      <DialogContent>
        {errorType === 'upload' && <UploadErrorContent patientId={patientId} />}
        {errorType === 'missing_t2' && <MissingT2ErrorContent patientId={patientId} />}
        {errorType === 'artefacts_detected' && <ArtefactsErrorContent patientId={patientId} />}
        {errorType === 'download' && <DownloadErrorContent patientId={patientId} />}
        {errorType === 'processing' && <ProcessingErrorContent stepLabel={stepLabel} patientId={patientId} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>CLOSE</Button>
      </DialogActions>
    </Dialog>
  );
}

ProcessingErrorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patientId: PropTypes.string,
  stepLabel: PropTypes.string,
  errorType: PropTypes.oneOf(['upload', 'download', 'processing']),
};
